import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  SnackbarList,
  Typography,
  useSnackbarContext,
  useFlags,
} from "@inductiveautomation/ws-components";

import Banner from "./components/Banner";
// import CertificationsWidget from "./components/CertificationsWidget";
import ElectiveStudiesWidget from "./components/ElectiveStudiesWidget";
import Footer from "./components/Footer";
import PublicAppBar from "./components/PublicAppBar";
import UserDataWidget from "./components/UserDataWidget";

import { getPublicProfileStyles } from "./_publicProfile.styles";
import { mockPublicProfile } from "./mocks/user";
import { useGetPublicProfileQuery } from "./api/publicProfile.api";
import { useCurrentUser } from "../../hooks/currentUser";
import {
  retrieveAuthChannel,
  CHANNEL_MESSAGE_TYPES,
} from "../../utilities/broadcastChannel";
import { AccountFlags } from "../../featureFlags.ts";

const PublicProfile = () => {
  const { t } = useTranslation("translation", { keyPrefix: "public_profile" });
  const { tsAccountPublicProfileFe } = useFlags<AccountFlags>();
  const navigate = useNavigate();
  const { snackbars, removeSnackbar } = useSnackbarContext();
  const { profileUrl } = useParams();
  const { classes } = getPublicProfileStyles();
  const { authChannel } = retrieveAuthChannel();

  const { data, error, isLoading } = useGetPublicProfileQuery(
    { publicId: profileUrl ?? "" },
    { skip: !profileUrl || !tsAccountPublicProfileFe }
  );
  const userInfo = useCurrentUser();

  useEffect(() => {
    authChannel.onmessage = ({ data }) => {
      // only refresh the page to clear out the session if the tab is not in focus
      if (
        !document.hasFocus() &&
        (data === CHANNEL_MESSAGE_TYPES.LOGOUT ||
          data === CHANNEL_MESSAGE_TYPES.LOGIN)
      ) {
        window.location.reload();
      }
    };

    return () => authChannel.close();
  }, [authChannel]);

  if (!tsAccountPublicProfileFe || !profileUrl || error) {
    void navigate("/404");
    return;
  }

  return (
    !isLoading &&
    data && (
      <div className={classes.root}>
        <PublicAppBar userInfo={userInfo} />

        <Banner />

        <div className={classes.contents}>
          <UserDataWidget user={data} />

          <Typography component="h2" className={classes.trainingHeading}>
            {t("sections.training.heading")}
          </Typography>

          <Typography variant="body1" className={classes.trainingDescription}>
            {t("sections.training.description")}
          </Typography>

          <div className={classes.trainingContainer}>
            {/* // TODO: revisit when we revisit public profile */}
            {/* <CertificationsWidget
              kind="certifications"
              certifications={mockPublicProfile.training.certifications}
            />

            <CertificationsWidget
              kind="credentials"
              certifications={mockPublicProfile.training.credentials}
            /> */}

            <ElectiveStudiesWidget
              courses={mockPublicProfile.training.electiveStudies}
            />
          </div>
        </div>

        <Footer />

        {createPortal(
          <div className={classes.snackbarPortal}>
            <SnackbarList
              snackbars={snackbars}
              removeSnackbar={removeSnackbar}
            />
          </div>,
          document.body
        )}
      </div>
    )
  );
};

export default PublicProfile;
